'use client';

import { stegaClean } from '@sanity/client/stega';

import { CTAWrapper } from '@/components/global/utils/CTAWrapper';
import { resolveI18nText } from '@/sanity/globals/I18n';
import type { GROQButton } from '@/sanity/lib/definitions';
import { cn } from '@/lib/utils';

/**
 * The page's button: an 11px-radius block with a triple chevron whose
 * opacity steps 0.2 → 0.6 → 1, and a soft inset glow (#4CC9A7) on the dark
 * colourway only. Figma: 18px Geist SemiBold, capitalised, 40/34 padding,
 * chevrons 24px overlapping by 13px. The label is cap-trimmed the way Figma
 * lays it out. For Geist the cap centre and the line-box centre coincide, so
 * this changes nothing visible; it keeps the box honest if the font changes.
 */
export default function ChevronCta({
  data,
  variant = 'dark',
  language,
  className,
}: {
  data?: GROQButton | null;
  variant?: 'dark' | 'white';
  language?: string;
  className?: string;
}) {
  const cta = stegaClean(data ?? {})?.cta;
  if (!cta) return null;

  const label = resolveI18nText(cta.title, cta.i18n, stegaClean(language));
  if (!label) return null;

  const dark = variant === 'dark';

  return (
    <CTAWrapper cta={cta} className={cn('w-full lg:!w-auto', className)}>
      <span
        className={cn(
          'flex relative justify-center items-center w-full rounded-[1.375rem] gap-[0.75rem] pl-[5rem] pr-[4.25rem] py-[2.25rem] transition-transform duration-300 hover:-translate-y-[0.25rem]',
          // The design has no glow on the white button.
          dark ? 'bg-[#145745] shadow-[inset_0_0_3.5125rem_#4CC9A7]' : 'bg-white',
        )}
      >
        <span
          className={cn(
            'capitalize text-center font-geist font-[600] text-[2.25rem] leading-[1.347] tracking-[-0.045rem] [text-box-trim:trim-both] [text-box-edge:cap_alphabetic]',
            dark ? 'text-white' : 'text-black',
          )}
        >
          {label}
        </span>
        <span className="flex relative items-center shrink-0">
          {[0.2, 0.6, 1].map((opacity, i) => (
            <svg
              key={opacity}
              viewBox="0 0 24 24"
              fill="none"
              aria-hidden
              className={cn('w-[3rem] h-[3rem] shrink-0', i < 2 && 'mr-[-1.625rem]')}
            >
              <path
                d="M16.5 12L9.00002 19.5L7.95002 18.45L14.4 12L7.95002 5.55L9.00002 4.5L16.5 12Z"
                fill={dark ? 'white' : 'black'}
                fillOpacity={opacity}
              />
            </svg>
          ))}
        </span>
      </span>
    </CTAWrapper>
  );
}
